import { useTranslation } from 'react-i18next'
import { ImagePlus, Loader2, Upload } from 'lucide-react'

import type { ProductionAsset, Shot } from '@/api/studio-production'
import { ProtoBtn, protoBtnClassName, ProtoModal } from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'

export function ShotFramePickerModal({
  shot,
  assets,
  assetsLoading,
  onClose,
  onUpload,
  onPickAsset,
}: {
  shot: Shot
  assets: ProductionAsset[]
  assetsLoading: boolean
  onClose: () => void
  onUpload: (file: File) => void
  onPickAsset: (url: string) => void
}) {
  const { t } = useTranslation()

  return (
    <ProtoModal
      open
      onClose={onClose}
      title={t('shots.framePickerTitle', { slug: shot.slug })}
      footer={
        <ProtoBtn size="sm" variant="secondary" onClick={onClose}>
          {t('common.cancel')}
        </ProtoBtn>
      }
    >
      <div className="flex gap-2 border-b border-border pb-3">
        <label className={protoBtnClassName({ size: 'sm', variant: 'secondary', className: 'flex-1 cursor-pointer' })}>
          <Upload className="h-3.5 w-3.5" />
          {t('shots.uploadFrame')}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) onUpload(f)
              e.target.value = ''
            }}
          />
        </label>
        <div className={protoBtnClassName({ size: 'sm', variant: 'soft', tone: 'amber', className: 'flex-1 pointer-events-none opacity-80' })}>
          <ImagePlus className="h-3.5 w-3.5" />
          {t('shots.pickAssetShort')}
        </div>
      </div>
      <div className="studio-scrollbar-hidden max-h-96 overflow-y-auto pt-3">
        {assetsLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
          </div>
        ) : assets.length === 0 ? (
          <p className="text-center text-xs text-muted-foreground">{t('shots.noAssets')}</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {assets.map((a) => (
              <Button
                key={a.public_id}
                type="button"
                variant="outline"
                onClick={() => onPickAsset(a.media_url ?? '')}
                className="h-auto flex-col overflow-hidden rounded-lg p-0 hover:border-indigo-500/40"
              >
                <img src={a.media_url} alt={a.name} className="aspect-video w-full object-cover" />
                <p className="w-full truncate p-1 text-[10px] font-normal text-muted-foreground">{a.name}</p>
              </Button>
            ))}
          </div>
        )}
      </div>
    </ProtoModal>
  )
}
